import { zodResolver } from '@hookform/resolvers/zod'
import { ChevronsUp, ChevronsDown } from 'lucide-react'
import React, { useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

import { useSendReferralStore } from 'src/features/business/referrals/stores/send-referral-store'
import { sendEvent } from 'src/shared/api/events/send-event'
import { Button } from 'src/shared/components/ui/buttons/button'
import { Form } from 'src/shared/components/ui/forms/form'
import { CountFormField } from 'src/shared/components/ui/forms/form-fields/count'
import { InputFormField } from 'src/shared/components/ui/forms/form-fields/input'
import { SelectFormField } from 'src/shared/components/ui/forms/form-fields/select'
import { TextareaFormField } from 'src/shared/components/ui/forms/form-fields/textarea'
import { FixedFooter } from 'src/shared/components/ui/miscellaneous/fixed-footer'
import { useMyUserData } from 'src/shared/features/users/api/accounts/use-my-user'
import {
  appraiserTransactionTypeSchema,
  appraiserTransactionTypeMap,
} from 'src/shared/schemas/referrals/referral-fields/appraiser/appraiser-transaction-type'
import { useEventStore } from 'src/shared/store/event-store'

const appraiserSchema = z.object({
  appraiserTransactionType: appraiserTransactionTypeSchema,
  // Optional fields
  bedrooms: z.number().min(0).optional(),
  bathrooms: z.number().min(0).optional(),
  squareFootageEstimate: z.number().max(120000).optional(),
  lotSize: z.number().max(300000).optional(),
  uniqueFeatures: z.string().max(1000).optional(),
  estimatedPropertyValue: z.number().max(1500000).optional(),
  approvedLoanAmount: z.number().max(1500000).optional(),
  downPaymentEstimate: z.number().min(1).optional(),
})

type AppraiserFormData = z.infer<typeof appraiserSchema>

export const AppraiserInfo: React.FC = () => {
  const referralFormData = useSendReferralStore((state) => state.referralFormData)
  const handleNext = useSendReferralStore((state) => state.handleNext)
  const handleBack = useSendReferralStore((state) => state.handleBack)
  const { data: currentUser } = useMyUserData()
  const eventStore = useEventStore()
  const sessionId = eventStore.sessionId
  const flowId = eventStore.flowId
  const selectedReferral = useSendReferralStore((state) => state.selectedReferral)
  const [showOptional, setShowOptional] = useState(false)

  const form = useForm<AppraiserFormData>({
    resolver: zodResolver(appraiserSchema),
    mode: 'onSubmit',
    defaultValues: {
      appraiserTransactionType:
        referralFormData.appraiserTransactionType || selectedReferral?.appraiserTransactionType || undefined,
      bedrooms: referralFormData.bedrooms || selectedReferral?.bedrooms || undefined,
      bathrooms: referralFormData.bathrooms || selectedReferral?.bathrooms || undefined,
      squareFootageEstimate:
        referralFormData.squareFootageEstimate || selectedReferral?.squareFootageEstimate || undefined,
      lotSize: referralFormData.lotSize || selectedReferral?.lotSize || undefined,
      uniqueFeatures: referralFormData.uniqueFeatures || selectedReferral?.uniqueFeatures || undefined,
      estimatedPropertyValue:
        referralFormData.estimatedPropertyValue || selectedReferral?.estimatedPropertyValue || undefined,
      approvedLoanAmount: referralFormData.approvedLoanAmount || selectedReferral?.approvedLoanAmount || undefined,
      downPaymentEstimate: referralFormData.downPaymentEstimate || selectedReferral?.downPaymentEstimate || undefined,
    },
  })

  const onSubmit = async (data: AppraiserFormData) => {
    if (currentUser) {
      sendEvent({
        userId: currentUser.id,
        eventType: 'flow_step',
        info: {
          sessionId,
          flowType: 'send_referral',
          flowStep: 'submit_appraiser_info',
          flowId,
          ...data,
        },
      })
    }

    handleNext(data)
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="mb-24 flex flex-col gap-1">
        <SelectFormField
          control={form.control}
          name="appraiserTransactionType"
          label="Appraisal Purpose"
          options={appraiserTransactionTypeMap}
          required
        />

        <Button
          variant="link"
          type="button"
          onClick={() => setShowOptional(!showOptional)}
          className="flex w-full items-center justify-center gap-2 text-muted hover:text-muted"
        >
          {showOptional ? <ChevronsUp size={20} /> : <ChevronsDown size={20} />} Optional Info{' '}
          {showOptional ? <ChevronsUp size={20} /> : <ChevronsDown size={20} />}
        </Button>

        {showOptional && (
          <>
            <div className="flex w-full flex-row justify-between gap-16">
              <CountFormField control={form.control} name="bedrooms" label="Bedroom(s)" />
              <CountFormField control={form.control} name="bathrooms" label="Bathroom(s)" increment={0.5} />
            </div>
            <div className="flex w-full flex-row justify-between gap-16">
              <InputFormField
                control={form.control}
                name="squareFootageEstimate"
                label="Square Footage"
                placeholder="Enter..."
                floatingLabel={false}
                type="number"
                max={120000}
                fieldClassName="bg-accent"
              />
              <InputFormField
                control={form.control}
                name="lotSize"
                label="Lot Size (sqft)"
                placeholder="Enter..."
                floatingLabel={false}
                type="number"
                max={300000}
                fieldClassName="bg-accent"
              />
            </div>
            <TextareaFormField
              control={form.control}
              name="uniqueFeatures"
              label="Unique Features or Renovations"
              description="e.g., pool, finished basement, recent updates..."
              minRows={2}
              maxLength={1000}
            />
            <InputFormField
              control={form.control}
              name="estimatedPropertyValue"
              label="Est. Property Value"
              placeholder="Enter..."
              floatingLabel={false}
              type="currency"
              max={1500000}
              fieldClassName="bg-accent"
            />
            <InputFormField
              control={form.control}
              name="approvedLoanAmount"
              label="Loan Amount or Requested Value"
              placeholder="Enter..."
              floatingLabel={false}
              type="currency"
              max={1500000}
              fieldClassName="bg-accent"
            />
            <InputFormField
              control={form.control}
              name="downPaymentEstimate"
              label="Down Payment"
              floatingLabel={false}
              type="currency"
              checkbox={{ label: 'Not sure', defaultChecked: false }}
            />
          </>
        )}

        <FixedFooter className="fixed inset-x-0">
          <div className="flex w-full gap-3">
            <Button variant="link" type="button" onClick={handleBack} className="w-full">
              Previous
            </Button>
            <Button type="submit" className="w-full">
              Next
            </Button>
          </div>
        </FixedFooter>
      </form>
    </Form>
  )
}
